import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PaymentPage.css';

const PaymentPage = () => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [userInfo, setUserInfo] = useState(null);

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get('order_id');
  const userId = urlParams.get('user_id');
  const plan = 'yearly'; // Only yearly plan available

  useEffect(() => {
    if (orderId && userId) {
      fetchPaymentDetails();
      fetchUserInfo();
    } else {
      setError('Missing required payment parameters');
    }
  }, [orderId, userId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/habitcircle/client/payment/get_subscription_plans`);
      const plans = response.data.data.plans;
      const selectedPlan = plans.find(p => p.plan_type === plan);
      
      if (selectedPlan) {
        setPaymentData(selectedPlan);
      } else {
        setError('Invalid subscription plan');
      }
    } catch (err) {
      setError('Failed to fetch payment details');
      console.error('Error fetching payment details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      // You might want to fetch user info from your API
      // For now, we'll use the userId from URL
      setUserInfo({ id: userId, name: 'User' });
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load Razorpay script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        setError('Failed to load payment gateway');
        return;
      }

      // Get Razorpay key from environment
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

      // Truncate description to 255 characters (Razorpay limit)
      // Ensure it's a string and truncate to 255 characters
      const truncatedDescription = String(paymentData.description || 'Payment').substring(0, 255);
      
      // Debug log to check description length
      if (paymentData.description && paymentData.description.length > 255) {
        console.warn('Description was truncated from', paymentData.description.length, 'to 255 characters');
      }

      // Final validation - ensure description is exactly 255 or less
      const finalDescription = truncatedDescription.length > 255 
        ? truncatedDescription.substring(0, 255) 
        : truncatedDescription;

      // Log final description for debugging
      console.log('Razorpay description length:', finalDescription.length, 'Description:', finalDescription);

      const options = {
        key: razorpayKey,
        amount: paymentData.amount * 100, // Convert to paise
        currency: paymentData.currency,
        name: 'HabitCircles',
        description: finalDescription,
        order_id: orderId,
        handler: async function (response) {
          try {
            // Verify payment on your backend
            const verifyResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/habitcircle/client/payment/verify_payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                user_id: userId
              }
            );

            if (verifyResponse.data.success) {
              setPaymentStatus('success');
              // Redirect to success page or show success message
              setTimeout(() => {
                window.close(); // Close the payment window
              }, 3000);
            } else {
              setError('Payment verification failed');
            }
          } catch (err) {
            setError('Payment verification failed');
            console.error('Payment verification error:', err);
          }
        },
        prefill: {
          name: userInfo?.name || 'User',
          email: userInfo?.email || '',
          contact: userInfo?.phone || ''
        },
        notes: {
          user_id: userId,
          plan_type: plan
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setPaymentStatus('cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      setError('Failed to initiate payment');
      console.error('Payment initiation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderPaymentStatus = () => {
    switch (paymentStatus) {
      case 'success':
        return (
          <div className="payment-success">
            <div className="success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p>Your subscription has been activated successfully.</p>
            <p>You can now close this window and return to the app.</p>
          </div>
        );
      case 'cancelled':
        return (
          <div className="payment-cancelled">
            <div className="cancel-icon">✕</div>
            <h2>Payment Cancelled</h2>
            <p>You cancelled the payment process.</p>
            <button onClick={handlePayment} className="retry-button">
              Try Again
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading && !paymentData) {
    return (
      <div className="payment-container">
        <div className="loading">Loading payment details...</div>
      </div>
    );
  }

  if (error && !paymentData) {
    return (
      <div className="payment-container">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success' || paymentStatus === 'cancelled') {
    return (
      <div className="payment-container">
        {renderPaymentStatus()}
      </div>
    );
  }

  return (
    <div className="payment-container">
      <div className="payment-header">
        {/* <h1>Complete Your Subscription</h1>
        <p>Unlock premium features for your habit tracking journey</p> */}
      </div>

      <div className="payment-card">
        <div className="plan-details">
          <h2>{paymentData?.name}</h2>
          <p className="plan-description">{paymentData?.description}</p>
          
          <div className="price-section">
            <span className="currency">₹</span>
            <span className="amount">{paymentData?.amount}</span>
            <span className="period">/year</span>
          </div>

          <div className="features-list">
            <h3>What you'll get:</h3>
            <ul>
              {paymentData?.features?.map((feature, index) => (
                <li key={index}>
                  <span className="feature-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="payment-methods">
          <h3>Payment Methods</h3>
          <div className="payment-options">
            <div className="payment-option">
              <span className="option-icon">💳</span>
              <span>Credit/Debit Cards</span>
            </div>
            <div className="payment-option">
              <span className="option-icon">📱</span>
              <span>UPI (Google Pay, PhonePe, Paytm)</span>
            </div>
            <div className="payment-option">
              <span className="option-icon">🏦</span>
              <span>Net Banking</span>
            </div>
            <div className="payment-option">
              <span className="option-icon">💼</span>
              <span>Digital Wallets</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button 
          onClick={handlePayment} 
          disabled={loading}
          className="pay-button"
        >
          {loading ? 'Processing...' : `Pay ₹${paymentData?.amount}/year`}
        </button>

        <div className="security-info">
          <p>🔒 Your payment is secure and encrypted</p>
          <p>🌍 International payments supported</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
